<script setup>
    import MainHeader from '@/components/HeaderComponent.vue';
    import SideMenu from '@/components/ManagementSideMenuComponent.vue';
    import { ref, onMounted, inject, getCurrentInstance } from 'vue';
    import { useManagementStore } from '@/stores/management/managementStore';
    import { useFileDownloadStore } from '@/stores/common/fileDownloadStore';
    import fileUploadDownloadService from '@/commonProject/service/fileUploadDownloadService';
    import Utils from '@/commonProject/helpers/utils.js';
    import Pagination from '@/components/PaginationComponent.vue';
    import i18n from '@/common/i18n';

    const app = getCurrentInstance();
    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    // 利用者の管理ストア
    const managementStore = useManagementStore();
    // ファイルダウンロードストア
    const fileDownloadStore = useFileDownloadStore();
    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');
    // ページングパーツ
    const currentPage = ref(1);
    const totalPages = ref(0);

    /**
     * 初期処理
     */
    onMounted(async () => {
        // 一覧取得
        await getList();
    });

    /**
     * 一覧取得処理
     */
    async function getList() {
        const maxPage = 50;
        const requestBody = {
            PageCond: {
                RowPosition: maxPage * (currentPage.value - 1) + 1,
                RowAmount: maxPage,
                RowCount: true,
            },
        };
        const response = await managementStore.postCsvImpLstAction(API_URL, requestBody);
        if (response.success) {
            totalPages.value = response.RowResult;
            handleOnloadPagination(totalPages.value);
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * ページ操作
     * @param newPage
     */
    async function handlePageChange(newPage) {
        currentPage.value = newPage;

        // ページング実行により、一覧取得APIを再実行する。
        await getList();
    }
    const pagination = ref();
    /**
     * ページングの初期処理を実行する。
     * 取得したAPIのデータ数を引き渡す。
     */
    function handleOnloadPagination(RowResult) {
        pagination.value.onload(RowResult);
    }

    /**
     * CSVダウンロード処理
     */
    async function downloadUCsv(CmpCsvImpHistPk, CmpCsvImpStatus, CmpCsvFileNm) {
        // バリデーションチェック
        if (isValidationError(CmpCsvImpStatus)) {
            return;
        }
        const requestBody = {
            CmpCsvImpHistPk: CmpCsvImpHistPk,
        };
        const response = await managementStore.postcsvFilDldAction(API_URL, requestBody);

        if (response.success) {
            /*
            const DldHistPk = response.responseBody.DldHistPk;
            if (!Utils.isNullEmpty(DldHistPk)) {
                // クエリパラメーターを作成する。
                const queryParam = {
                    DldHistPk: DldHistPk, //ダウンロード履歴PK
                };

                let result = null;
                downloadCheck: do {
                    // ダウンロード履歴取得APIをコールする。
                    const response2 = await fileDownloadStore.getDownloadFileHistoryPkFromApi(API_URL, queryParam);
                    if (!response2.success) {
                        messageDialog('error', response2.errorMessage);
                        if (response2.redirect) {
                            location.href = response2.redirectUrl;
                        }
                    }

                    result = await fileUploadDownloadService.fileDownload(response2.responseBody.DldStatus, response2.responseBody.DldS3Url, CmpCsvFileNm);
                    switchByResult: switch (result) {
                        case 'startDownload':
                            {
                                const requestBody2 = {
                                    DldHistPk: DldHistPk,
                                    DldStatus: '3',
                                };
                                const response3 = await fileDownloadStore.updateDownloadFileHistoryPkFromApi(API_URL, requestBody2);
                                if (!response3.success) {
                                    messageDialog('error', response3.errorMessage);
                                    if (response3.redirect) {
                                        location.href = response3.redirectUrl;
                                    }
                                }
                                result = 'continue';
                            }
                            break switchByResult;
                        case 'endDownload':
                            {
                                const requestBody2 = {
                                    DldHistPk: DldHistPk,
                                    DldStatus: '4',
                                };
                                const response3 = await fileDownloadStore.updateDownloadFileHistoryPkFromApi(API_URL, requestBody2);
                                if (!response3.success) {
                                    messageDialog('error', response3.errorMessage);
                                    if (response3.redirect) {
                                        location.href = response3.redirectUrl;
                                    }
                                }
                                result = 'continue';
                            }
                            break switchByResult;
                        case 'error':
                            break downloadCheck;
                        case 'break':
                            break downloadCheck;
                        default:
                            continue downloadCheck;
                    }
                } while (result === 'continue');
            }
            */
            const DldHistPk = response.responseBody.DldHistPk;
            if (!Utils.isNullEmpty(DldHistPk)) {
                // ダウンロード履歴取得APIのクエリパラメーターを生成する。
                const queryParamDldHistGet = {
                    DldHistPk: DldHistPk, //ダウンロード履歴PK
                };

                // 念のためタイマーイベントを全てクリアする。
                // 万が一実行中のタイマーイベントが残っている場合は強制停止する。
                clearTimeout();

                // タイマーで監視し、APIのエラー時のループを制御する。
                let isDownloadTimeoutError = false;

                // ダウンロード処理開始後からダウンロード処理が完了せずに15分経過した場合は、エラーとしてダウンロード処理を停止する。
                const downloadTotalErrorTimer = 900000;

                // ダウンロード履歴取得APIをコールする間隔のタイマー値を設定する。
                // ダウンロード可能かどうかのチェック処理後、2秒間のインターバルを設けて、再度APIをコールする。
                const callDownloadHistoryApiintervalTimer = 2000;

                // ダウンロード対象のファイル名を宣言する。
                const fileName = CmpCsvFileNm;

                // ダウンロード処理の総経過時間を監視し、15分経過した場合に呼び出されるファンクションを宣言する。
                const checkTimeout = function () {
                    // ダウンロード総経過時間監視フラグを有効にして、ダウンロード限界時間経過としてエラー発生扱いにする。
                    isDownloadTimeoutError = true;

                    // 念のためタイマーイベントを全てクリアする。
                    // 万が一実行中のタイマーイベントが残っている場合は強制停止する。
                    clearTimeout();
                };

                // ダウンロード処理構造体を宣言する。
                // ダウンロード履歴取得API、ダウンロード履歴更新APIの実行タイミングを制御し、ダウンロード可能なファイルが存在する場合はJavascriptでダウンロードを実行する。
                const downloadEventPromise = async function (id) {
                    await new Promise(function (resolve, reject) {
                        // ダウンロード履歴取得APIから、ダウンロード対象ファイルの状態を取得し、本処理で判定して制御する。

                        // もし、ダウンロード総経過時間フラグが有効の場合は、以降の処理を実行せず、ダウンロード限界時間経過としてエラーにして、rejectで返却する。
                        if (isDownloadTimeoutError) {
                            reject('download timeout error');
                            return;
                        }

                        // ダウンロード履歴取得APIの判定したダウンロードファイルの状態に応じて制御する。
                        // ファイルダウンロード処理Javascript側で状況を判定している。
                        switch (id) {
                            case 'continue':
                            case 'startDownload':
                            case 'endDownload':
                                // ダウンロード準備中。
                                // ダウンロード可能状態の応答が返ってきた。
                                // Javascriptでファイルをダウンロードして、正常に完了した状態。
                                resolve(id);
                                break;
                            case 'error':
                            case 'break':
                            default:
                                // ダウンロード履歴取得APIでダウンロードエラーが発生。
                                // ダウンロード履歴取得APIからダウンロード完了が返却された場合。
                                // 上記以外の本処理内での例外の場合。
                                // ダウンロード処理を停止するためにrejectで返却する。
                                reject(id);
                                break;
                        }
                    })
                        .then(async function (resolved) {
                            // Promiseでresolveの正常応答の場合。

                            // もし、ダウンロード総経過時間フラグが有効の場合は、以降の処理を実行しない。
                            if (isDownloadTimeoutError) {
                                return;
                            }

                            // ダウンロード履歴取得APIから取得したステータスと、ダウンロード処理スクリプトで判定した結果に応じて制御する。
                            switch (resolved) {
                                case 'startDownload':
                                    {
                                        // ダウンロード履歴取得APIからダウンロード可能ステータスが返却されたので、Javascriptでファイルダウンロードを実行した場合。

                                        // ダウンロード履歴更新APIのリクエストボディを生成する。
                                        const requestBodyDldHistPut = {
                                            DldHistPk: DldHistPk,
                                            DldStatus: '3', // ダウンロード中モードに設定する。
                                        };

                                        // ダウンロード履歴更新APIをコールする。
                                        const responseDldHistPut = await fileDownloadStore.updateDownloadFileHistoryPkFromApi(API_URL, requestBodyDldHistPut);

                                        // APIの応答結果に応じて制御する。
                                        if (responseDldHistPut.success) {
                                            // APIの正常応答を検知して、再度本ダウンロードPromiseを実行する。
                                            await downloadEventPromise('continue');
                                        } else {
                                            // エラーの場合。
                                            messageDialog('error', responseDldHistPut.errorMessage);
                                            if (responseDldHistPut.redirect) {
                                                location.href = responseDldHistPut.redirectUrl;
                                            }
                                        }
                                    }
                                    break;
                                case 'endDownload':
                                    {
                                        // Javascriptでファイルダウンロードを実行した場合。

                                        // ダウンロード履歴更新APIのリクエストボディを生成する。
                                        const requestBodyDldHistPut = {
                                            DldHistPk: DldHistPk,
                                            DldStatus: '4', // ダウンロード完了に設定する。
                                        };

                                        // ダウンロード履歴更新APIをコールする。
                                        const responseDldHistPut = await fileDownloadStore.updateDownloadFileHistoryPkFromApi(API_URL, requestBodyDldHistPut);

                                        // APIの応答結果に応じて制御する。
                                        if (responseDldHistPut.success) {
                                            // APIの正常応答を検知して、再度本ダウンロードPromiseを実行する。
                                            await downloadEventPromise('continue');
                                        } else {
                                            // エラーの場合。
                                            messageDialog('error', responseDldHistPut.errorMessage);
                                            if (responseDldHistPut.redirect) {
                                                location.href = responseDldHistPut.redirectUrl;
                                            }
                                        }
                                    }
                                    break;
                                case 'continue':
                                    // ダウンロード履歴取得APIを指定のインターバルの時間経過後に再度コールするタイマーイベント。
                                    setTimeout(async function () {
                                        // ダウンロード履歴取得APIをコールする。
                                        const responseDldHistGet2 = await fileDownloadStore.getDownloadFileHistoryPkFromApi(API_URL, queryParamDldHistGet);

                                        // APIの応答結果に応じて制御する。
                                        if (responseDldHistGet2.success) {
                                            // ファイルダウンロードスクリプトを実行する。
                                            // ダウンロード履歴取得APIから取得したダウンロード状態、ダウンロードファイルの座標、ダウンロード対象のファイル名を引数に設定する。
                                            const fileUploadDownloadServiceResult2 = await fileUploadDownloadService.fileDownload(responseDldHistGet2.responseBody.DldStatus, responseDldHistGet2.responseBody.DldS3Url, fileName);

                                            // ファイルダウンロードスクリプト実行後、再度本ダウンロードPromiseを実行する。
                                            await downloadEventPromise(fileUploadDownloadServiceResult2);
                                        } else {
                                            // ダウンロード履歴取得APIがエラーの場合。
                                            messageDialog('error', responseDldHistGet2.errorMessage);
                                            if (responseDldHistGet2.redirect) {
                                                location.href = responseDldHistGet2.redirectUrl;
                                            }
                                        }
                                    }, callDownloadHistoryApiintervalTimer);
                                    break;
                                default:
                                    // 何もしない
                                    break;
                            }
                        })
                        .catch(function (/*rejected*/) {
                            // Promiseでrejectのルートで処理された場合。

                            // ダウンロードが完了した、や、エラーが発生したので、ダウンロード総経過時間フラグを更新して、以降の処理を実行しないようにする。
                            isDownloadTimeoutError = true;

                            // 念のためタイマーイベントを全てクリアする。
                            // 万が一実行中のタイマーイベントが残っている場合は強制停止する。
                            clearTimeout();
                        });
                };

                // ***** ダウンロードの初期処理を実行する - ここから - **** //

                // ダウンロード総時間を監視する。
                setTimeout(checkTimeout, downloadTotalErrorTimer);

                // 初回のダウンロード履歴取得APIをコールする。
                const responseDldHistGet = await fileDownloadStore.getDownloadFileHistoryPkFromApi(API_URL, queryParamDldHistGet);

                // APIの応答結果に応じて制御する。
                if (responseDldHistGet.success) {
                    // ファイルダウンロードスクリプトを実行する。
                    // ダウンロード履歴取得APIから取得したダウンロード状態、ダウンロードファイルの座標、ダウンロード対象のファイル名を引数に設定する。
                    const fileUploadDownloadServiceResult = await fileUploadDownloadService.fileDownload(responseDldHistGet.responseBody.DldStatus, responseDldHistGet.responseBody.DldS3Url, fileName);

                    // ファイルダウンロードスクリプト実行後、ファイルダウンロード処理Promiseを実行する。
                    await downloadEventPromise(fileUploadDownloadServiceResult);
                } else {
                    // ダウンロード履歴取得APIがエラーの場合。
                    messageDialog('error', responseDldHistGet.errorMessage);
                    if (responseDldHistGet.redirect) {
                        location.href = responseDldHistGet.redirectUrl;
                    }
                }

                // ***** ダウンロードの初期処理を実行する - ここまで - **** //
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }

    /**
     * バリデーションチェック
     */
    function isValidationError(CmpCsvImpStatus) {
        // エラーメッセージを初期化する。
        let message = '';
        // ID
        if (CmpCsvImpStatus !== '5') {
            message += i18n.global.t('ManagementUpload.message.message1') + '\n';
        }
        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!Utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }
</script>

<template>
    <section id="main">
        <MainHeader />
        <div id="contents" class="sideMenu headerSingleBlock">
            <SideMenu />
            <div id="rightBlock" class="noPaddingTop">
                <div class="whiteBox wide">
                    <h3 class="textCenter">
                        <span>{{ $t('ManagementUpload.parts.pageTitle1') }} </span>
                    </h3>
                    <section class="whiteBoxContents">
                        <article class="innerTable">
                            <table class="table noSelection" id="sample">
                                <thead>
                                    <tr>
                                        <th style="width: 114px">
                                            <!--日時-->
                                            <span>{{ $t('ManagementUpload.parts.tableHeader1') }} </span>
                                        </th>
                                        <th style="width: 150px">
                                            <!--作成者-->
                                            <span>{{ $t('ManagementUpload.parts.tableHeader2') }} </span>
                                        </th>
                                        <th style="width: 100px">
                                            <!--データ種別-->
                                            <span>{{ $t('ManagementUpload.parts.tableHeader3') }} </span>
                                        </th>
                                        <th style="width: 240px">
                                            <!--ファイル名-->
                                            <span>{{ $t('ManagementUpload.parts.tableHeader4') }} </span>
                                        </th>
                                        <th style="width: 70px">
                                            <!--ステータス-->
                                            <span>{{ $t('ManagementUpload.parts.tableHeader5') }} </span>
                                        </th>
                                        <th style="width: auto">
                                            <!--エラー情報-->
                                            <span>{{ $t('ManagementUpload.parts.tableHeader6') }} </span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="contents in managementStore.csvImportList" :key="contents.index">
                                        <td>
                                            <template v-if="!Utils.isNullEmpty(contents.CmpCsvImpDtE)">
                                                <span>{{ contents.CmpCsvImpDtE.slice(0, -3) }}</span>
                                            </template>
                                        </td>
                                        <td>
                                            <span>{{ contents.UserNm }}</span>
                                        </td>
                                        <td>
                                            <span v-if="contents.CmpCsvImpDataType === '01'">{{ $t('ManagementUpload.parts.dl1') }} </span>
                                            <span v-else-if="contents.CmpCsvImpDataType === '02'">{{ $t('ManagementUpload.parts.dl2') }} </span>
                                            <span v-else-if="contents.CmpCsvImpDataType === '03'">{{ $t('ManagementUpload.parts.dl3') }} </span>
                                            <span v-else-if="contents.CmpCsvImpDataType === '04'">{{ $t('ManagementUpload.parts.dl4') }} </span>
                                            <span v-else-if="contents.CmpCsvImpDataType === '05'">{{ $t('ManagementUpload.parts.dl5') }} </span>
                                            <span v-else-if="contents.CmpCsvImpDataType === '06'">{{ $t('ManagementUpload.parts.dl6') }} </span>
                                            <span v-else-if="contents.CmpCsvImpDataType === '07'">{{ $t('ManagementUpload.parts.dl7') }} </span>
                                        </td>
                                        <td>
                                            <button type="button" class="textLink" style="text-align: left" @click="downloadUCsv(contents.CmpCsvImpHistPk, contents.CmpCsvImpStatus, contents.CmpCsvFileNm)">{{ contents.CmpCsvFileNm }}</button>
                                        </td>
                                        <td>
                                            <span v-if="contents.CmpCsvImpStatus === '1'">{{ $t('ManagementUpload.parts.dl8') }} </span>
                                            <span v-if="contents.CmpCsvImpStatus === '4'">{{ $t('ManagementUpload.parts.dl9') }} </span>
                                            <span v-if="contents.CmpCsvImpStatus === '5'">{{ $t('ManagementUpload.parts.dl10') }} </span>
                                            <span v-if="contents.CmpCsvImpStatus === '6'">{{ $t('ManagementUpload.parts.dl11') }} </span>
                                        </td>
                                        <td>
                                            <span>{{ contents.CmpCsvImpErrInfo }}</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </article>
                        <Pagination ref="pagination" :currentPage="currentPage" :totalPages="totalPages" @pageChanged="handlePageChange" />
                    </section>
                    <section class="whiteBoxFooter">
                        <ul class="btnArea center">
                            <RouterLink :to="{ name: 'home' }">
                                <!--戻る-->
                                <button type="button" id="btnBack" class="btnNormal">{{ $t('ManagementUpload.parts.button1') }}</button>
                            </RouterLink>
                        </ul>
                    </section>
                </div>
            </div>
        </div>
    </section>
</template>

<style></style>
